import moment from 'moment';
import {
	COVERAGE_PARTIAL_PCT,
	COVERAGE_VERIFIED_PCT,
	DISTANCE_SOURCE_TAG,
	HALT_TYPE,
	TERMINAL_TRIP_STATUS_IDS,
	THRESHOLD_BUFFER_PCT,
	VARIANCE_FLAG_PCT,
} from './constants';

/**
 * Build the Distance tab's view model.
 *
 * One pure function, two inputs, one output shape — so every section component reads a
 * settled object and never has to know which API a number came from:
 *
 *   trackingInfo   `all_tracking_info`, already fetched and polled by TripOverviewPageV2.
 *   tripDetails    The trip row (odometer readings, type, validity, vendor).
 *   reconciliation `search_type: trip_distance_reconciliation`. Wins every field it
 *                  carries; `all_tracking_info` fills the rest.
 *
 * Two things the reconciliation API deliberately does NOT carry, so the fallback is
 * load-bearing rather than legacy scaffolding:
 *   - `planned_distance_km` is persisted as null for every trip (the backend's
 *     `reconcile_trip_distance` hard-codes it), so Planned — and the threshold derived
 *     from it — always come from `all_tracking_info`.
 *   - the halt list depends on the backend version. Newer backends send `halts` (every
 *     halt, classified, with node names) and that is used as-is. Older ones send
 *     `unscheduled_halts` ONLY, leaving dwell and in-transit as aggregate minutes with no
 *     rows behind them - so per-node dwell is rebuilt from node arrival/departure stamps
 *     and merged in, and the table marks itself partial because in-transit halts cannot
 *     be reconstructed at all.
 *
 * Anything this function cannot honestly compute is `null`. `null` and `0` are kept
 * strictly apart throughout: "never measured" and "measured as nothing" are different
 * answers, and collapsing them is the ambiguity this tab exists to remove.
 */

const num = (value) => {
	if (value === null || value === undefined || value === '' || value === '-') return null;
	const parsed = Number(value);
	return Number.isFinite(parsed) ? parsed : null;
};

/** Tracking timestamps are naive ISO strings that are already UTC. Read them as such. */
const utc = (value) => {
	if (!value) return null;
	const parsed = moment.utc(value);
	return parsed.isValid() ? parsed : null;
};

const secondsBetween = (from, to) => {
	if (!from || !to) return null;
	const seconds = to.diff(from, 'seconds');
	return seconds >= 0 ? seconds : null;
};

/** The API speaks minutes throughout; every component here speaks seconds. */
const minutesToSeconds = (value) => {
	const parsed = num(value);
	return parsed === null ? null : Math.round(parsed * 60);
};

/** `gps_partial_odometer_fallback` → the tab's own tag vocabulary. */
const API_TAG_CODE = {
	gps_verified: DISTANCE_SOURCE_TAG.GPS_VERIFIED,
	gps_partial_odometer_fallback: DISTANCE_SOURCE_TAG.GPS_PARTIAL,
	odometer_only: DISTANCE_SOURCE_TAG.ODOMETER_ONLY,
	manual_override: DISTANCE_SOURCE_TAG.MANUAL_OVERRIDE,
};

/**
 * The denominator for every percentage on this tab.
 *
 * An open trip has no end, so `now` stands in and `endIsNow` says so — without that flag
 * a coverage figure silently drifts downward while the page sits open and looks like a
 * degrading signal rather than a moving goalpost.
 */
function buildWindow(trackingInfo, reconciliation) {
	const timings = trackingInfo?.trip_timings || {};
	const start = utc(timings.actual_start_time) || utc(timings.start_time);
	const actualEnd = utc(timings.actual_end_time) || utc(timings.end_time);
	const end = actualEnd || moment.utc();

	// The API's own total is authoritative when present — it is the denominator the
	// backend used for coverage_pct, and a locally recomputed one can differ by seconds.
	const apiSeconds = minutesToSeconds(
		reconciliation?.stoppage_summary?.total_trip_duration_minutes
	);

	return {
		start,
		end: start ? end : null,
		endIsNow: !actualEnd,
		totalSeconds: apiSeconds || secondsBetween(start, start ? end : null),
	};
}

/**
 * Odometer distance and the readings behind it.
 *
 * Prefers the computed distance and falls back to the raw readings only when there is
 * none. A negative delta is a data-entry error, not a short trip — it yields a null
 * distance and an `invalid` flag the card surfaces, rather than being clamped to zero.
 */
function resolveOdometer(trackingInfo, tripDetails, d) {
	const start = num(tripDetails?.startKm ?? trackingInfo?.trip_info?.start_km);
	const end = num(tripDetails?.endKm ?? trackingInfo?.trip_info?.end_km);
	const delta = start !== null && end !== null ? end - start : null;
	const stored = num(d?.odometer_distance_km ?? trackingInfo?.odometer_distance_km);

	return {
		start,
		end,
		km: stored ?? (delta !== null && delta >= 0 ? delta : null),
		invalid: delta !== null && delta < 0,
	};
}

/**
 * |GPS − Odometer| / Odometer, as a percentage. Null unless both sides are real AND GPS
 * is a complete measurement of the trip (coverage in the GPS Verified band).
 *
 * That gate matters even though the backend now applies the same rule itself: `supplied`
 * is only trusted when non-null, but the client-side fallback below used to recompute
 * unconditionally whenever the backend sent null - which reintroduced the exact bug the
 * gate exists to prevent, purely on this side. Below GPS Verified coverage, gpsKm is a
 * floor (see isPartialTrail below), not the trip's distance - comparing it against the
 * odometer measures missing coverage, not disagreement, and at 0% coverage it is exactly
 * 100% for every trip, carrying no information. Coverage is already shown separately.
 */
function resolveVariance(gpsKm, odometerKm, d) {
	const supplied = num(d?.variance_pct);
	if (supplied !== null) return supplied;
	if (gpsKm === null || odometerKm === null || odometerKm <= 0) return null;
	const coveragePercent = num(d?.gps_coverage_pct);
	if (coveragePercent === null || coveragePercent < COVERAGE_VERIFIED_PCT) return null;
	return (Math.abs(gpsKm - odometerKm) / odometerKm) * 100;
}

/**
 * Route-adherence check: planned distance plus a fixed allowance, and whether the final
 * distance stayed under it.
 *
 * Computed here because the API carries no threshold at all. Distinct from variance:
 * variance asks "do our two measurements of this trip agree", the threshold asks "did
 * this trip run longer than the route it was quoted on" — a trip can be GPS-verified to
 * the metre and still blow through its threshold.
 */
function resolveThreshold(plannedKm, finalKm) {
	const distanceKm = plannedKm === null ? null : plannedKm * (1 + THRESHOLD_BUFFER_PCT / 100);
	if (distanceKm === null || finalKm === null) {
		return { distanceKm, basisKm: plannedKm, bufferPct: THRESHOLD_BUFFER_PCT, status: null };
	}
	return {
		distanceKm,
		basisKm: plannedKm,
		bufferPct: THRESHOLD_BUFFER_PCT,
		status: finalKm <= distanceKm ? 'within' : 'exceeds',
	};
}

function buildDistances(trackingInfo, tripDetails, reconciliation) {
	const d = reconciliation?.distance;
	const odometer = resolveOdometer(trackingInfo, tripDetails, d);
	const gpsKm = num(d?.gps_tracked_distance_km ?? trackingInfo?.accurate_distance_km);
	const variancePct = resolveVariance(gpsKm, odometer.km, d);
	// Always from all_tracking_info: the reconciliation row stores planned as null.
	// Zero is treated as absent: a route with no planned distance reads as 0 here, and
	// carrying it through produces a "Threshold 0 km (Planned 0 km + 10%)" row that looks
	// like a real limit the trip has already blown through.
	const plannedRaw = num(trackingInfo?.planned_distance_km);
	const plannedKm = plannedRaw !== null && plannedRaw > 0 ? plannedRaw : null;
	const finalKm = num(d?.final_distance_km ?? trackingInfo?.final_km);

	return {
		plannedKm,
		gpsKm,
		odometerKm: odometer.km,
		odometerStart: odometer.start,
		odometerEnd: odometer.end,
		odometerInvalid: odometer.invalid,
		virtualKm: num(trackingInfo?.virtual_distance_km),
		finalKm,
		finalSource: trackingInfo?.final_source ?? null,
		// driver_app | admin_manual | gps_auto — who supplied the closing odometer reading.
		endKmSource: d?.end_km_source ?? null,
		threshold: resolveThreshold(plannedKm, finalKm),
		variancePct,
		varianceFlagged:
			d?.high_variance_flag ?? (variancePct !== null && variancePct > VARIANCE_FLAG_PCT),
	};
}

/** One GPS health segment, normalized from minutes/snake_case into the tab's shape. */
function normalizeSegment(segment, index) {
	const lat = num(segment?.last_known_latitude);
	const lng = num(segment?.last_known_longitude);

	return {
		key: `${segment?.type}-${segment?.start_time ?? index}`,
		type: segment?.type === 'gap' ? 'gap' : 'active',
		start: segment?.start_time ?? null,
		end: segment?.end_time ?? null,
		durationSeconds: minutesToSeconds(segment?.duration_minutes),
		// Only a gap carries one, and a leading gap carries none — there is no earlier fix.
		lastFix: lat !== null && lng !== null ? { lat, lng, source: segment?.last_source } : null,
	};
}

function buildGps(trackingInfo, reconciliation, windowInfo) {
	const segments = Array.isArray(reconciliation?.gps_health_timeline)
		? reconciliation.gps_health_timeline.map(normalizeSegment)
		: null;
	// The backend floors coverage at 0.0 whenever the trip window is zero-length
	// (_safe_timeline_and_duration returns `{segments: [], coverage_pct: 0.0}` before it
	// looks at a single ping). That 0 means "nothing to measure against", not "measured
	// and found none" — only a real window makes 0% a fact worth printing.
	const measurable = (windowInfo?.totalSeconds || 0) > 0;
	const coveragePercent = measurable ? num(reconciliation?.distance?.gps_coverage_pct) : null;
	const coverageSeconds = segments
		? segments
				.filter((segment) => segment.type === 'active')
				.reduce((total, segment) => total + (segment.durationSeconds || 0), 0)
		: null;

	return {
		// `distance.gps_installed` — whether this vehicle even has a GPS box. Older
		// backends (and trips fetched before the paired backend fix ships) omit it, so the
		// fallback keeps "unknown" honest instead of reading a missing field as false.
		installed: reconciliation?.distance?.gps_installed ?? null,
		coveragePercent,
		coverageSeconds,
		segments: segments && segments.length ? segments : null,
		// Drives the "(partial trail)" note beside the GPS distance: the trail is real but
		// does not cover the whole trip, so the km it reports is a floor, not the distance.
		isPartialTrail: coveragePercent !== null && coveragePercent < COVERAGE_VERIFIED_PCT,
		// Describes *now*, not the trip — kept only for the pre-reconciliation fallback.
		liveStatus: trackingInfo?.gps_status || null,
		lastPingDisplay: trackingInfo?.last_location_ping || null,
	};
}

/**
 * Dwell rows, from node arrival/departure timestamps.
 *
 * The fallback for backends that send `unscheduled_halts` without `halts`: dwell exists
 * there as an aggregate minute count with no rows behind it, and the design's table lists
 * every stop by name. Unused when the API sends the full `halts` list, whose dwell rows
 * come from the same classification that produced the minute totals — so the rows and the
 * proportion bar cannot disagree.
 */
function buildNodeDwellHalts(trackingInfo) {
	const nodes = Array.isArray(trackingInfo?.nodes) ? trackingInfo.nodes : [];

	return nodes
		.map((node) => {
			const arrival = utc(node?.arrival_time);
			const departure = utc(node?.departure_time);
			const duration = secondsBetween(arrival, departure);
			if (duration === null || duration <= 0) return null;

			return {
				key: `node-${node?.sequence_order ?? node?.node_id}`,
				type: HALT_TYPE.DWELL,
				fromNodeTimestamps: true,
				start: node.arrival_time,
				end: node.departure_time,
				durationSeconds: duration,
				nodeName: node?.node_name || null,
				// The table labels these "Dwell (Node 1)" — the sequence is what identifies a
				// stop on a round trip, where both stops share one node_id and name.
				nodeSequence: node?.sequence_order ?? null,
				lat: num(node?.latitude),
				lng: num(node?.longitude),
			};
		})
		.filter(Boolean);
}

/**
 * The stop's position in the route, for a halt that matched a node.
 *
 * The design labels dwell rows "Dwell (Node 1)", but a halt carries node_id — which node
 * it was — not which stop. A round trip revisits one node as two stops sharing that id,
 * so the arrival time decides which one this halt belongs to.
 */
function resolveNodeSequence(halt, nodes) {
	if (halt?.node_id === null || halt?.node_id === undefined) return null;

	const matches = nodes.filter((node) => node?.node_id === halt.node_id);
	if (!matches.length) return null;

	const haltStart = Date.parse(halt?.start_time ?? '');
	if (matches.length === 1 || Number.isNaN(haltStart)) {
		return matches[0]?.sequence_order ?? null;
	}

	const nearest = matches.reduce((best, node) => {
		const arrival = Date.parse(node?.arrival_time ?? '');
		if (Number.isNaN(arrival)) return best;
		const gap = Math.abs(arrival - haltStart);
		return best === null || gap < best.gap ? { node, gap } : best;
	}, null);

	return (nearest ? nearest.node : matches[0])?.sequence_order ?? null;
}

/**
 * Every halt the API reports, in one list — dwell, in-transit and unscheduled alike.
 *
 * Preferred over buildNodeDwellHalts + buildUnscheduledHalts whenever the backend sends
 * `halts`: those two exist only because the API used to return `unscheduled_halts` alone,
 * so dwell had to be reconstructed from node timestamps and in-transit halts could not be
 * shown at all. This list is the backend's own classification, which is the one that
 * produced the minute totals in the same payload — so the rows and the bar agree.
 *
 * node_name is null for anything that matched no node; the table falls back to
 * coordinates, which is the honest label for "stationary here, no idea what this place is".
 */
function buildApiHalts(summary, trackingInfo) {
	const rows = Array.isArray(summary?.halts) ? summary.halts : [];
	const nodes = Array.isArray(trackingInfo?.nodes) ? trackingInfo.nodes : [];

	return rows.map((halt, index) => ({
		key: `halt-${halt?.start_time ?? index}-${index}`,
		type: halt?.halt_type ?? null,
		start: halt?.start_time ?? null,
		end: halt?.end_time ?? null,
		durationSeconds: minutesToSeconds(halt?.duration_minutes),
		nodeName: halt?.node_name || null,
		// Reverse-geocoded, only ever present when node_name is not: a named node is
		// ours and authoritative, a geocoded address is a best guess at the same spot.
		locationName: halt?.location_name || null,
		nodeSequence: resolveNodeSequence(halt, nodes),
		lat: num(halt?.latitude),
		lng: num(halt?.longitude),
		sourceConfirmed: Boolean(halt?.source_confirmed),
	}));
}

/** The API's unscheduled long halts, normalized into the same row shape as node dwell. */
function buildUnscheduledHalts(summary) {
	const rows = Array.isArray(summary?.unscheduled_halts) ? summary.unscheduled_halts : [];

	return rows.map((halt, index) => ({
		key: `unscheduled-${halt?.start_time ?? index}`,
		type: HALT_TYPE.UNSCHEDULED_LONG,
		start: halt?.start_time ?? null,
		end: halt?.end_time ?? null,
		durationSeconds: minutesToSeconds(halt?.duration_minutes),
		nodeName: null,
		nodeSequence: null,
		lat: num(halt?.latitude),
		lng: num(halt?.longitude),
		// The device itself reported Halted/Idling, rather than us inferring it from
		// position deltas — worth showing, it is the difference between evidence and inference.
		sourceConfirmed: Boolean(halt?.source_confirmed),
	}));
}

/**
 * Segments for the stoppage proportion bar, in bar order.
 *
 * Whatever the known bands do not account for becomes an `unmeasured` remainder rather
 * than being absorbed into moving time. With the API answering, that remainder is the
 * backend's own `total_untracked_gap_minutes` — time where no source pinged at all, which
 * is neither moving nor stopped but genuinely unknown.
 */
function buildStoppageBar({ totalSeconds, moving, dwell, inTransit, unscheduled, untracked }) {
	if (!totalSeconds) return [];

	const known = [
		{ key: 'moving', seconds: moving },
		{ key: 'dwell', seconds: dwell },
		{ key: 'halt', seconds: inTransit },
		{ key: 'unscheduled', seconds: unscheduled },
		{ key: 'unmeasured', seconds: untracked },
	].filter((segment) => segment.seconds !== null && segment.seconds > 0);

	const accounted = known.reduce((total, segment) => total + segment.seconds, 0);
	const remainder = totalSeconds - accounted;

	// Nothing reported an untracked figure, yet the bands do not fill the bar — the rest
	// is unmeasured by definition.
	if (remainder > 0 && untracked === null) {
		return [...known, { key: 'unmeasured', seconds: remainder }];
	}
	return known;
}

function buildStoppages(trackingInfo, reconciliation, windowInfo) {
	const summary = reconciliation?.stoppage_summary;
	const nodeDwell = buildNodeDwellHalts(trackingInfo);

	if (!summary) {
		const dwellSeconds = nodeDwell.length
			? nodeDwell.reduce((total, halt) => total + halt.durationSeconds, 0)
			: null;

		return {
			totalSeconds: windowInfo.totalSeconds,
			movingSeconds: null,
			dwellSeconds,
			inTransitSeconds: null,
			unscheduledSeconds: null,
			untrackedSeconds: null,
			unscheduledCount: null,
			halts: nodeDwell,
			// Says out loud that everything except node dwell is missing, so the section can
			// label itself rather than implying the trip had no halts.
			partial: true,
			bar: buildStoppageBar({
				totalSeconds: windowInfo.totalSeconds,
				moving: null,
				dwell: dwellSeconds,
				inTransit: null,
				unscheduled: null,
				untracked: null,
			}),
		};
	}

	// `halts` is the newer, complete list. Older backends send only `unscheduled_halts`,
	// so fall back to reconstructing dwell from node timestamps — which cannot describe
	// in-transit halts, hence `partial` below.
	const apiHalts = Array.isArray(summary.halts) ? buildApiHalts(summary, trackingInfo) : null;
	const gpsHalts = apiHalts && apiHalts.length ? apiHalts : null;
	const usingNodeFallback = !gpsHalts && nodeDwell.length > 0;

	const unscheduled = buildUnscheduledHalts(summary);
	const totalSeconds =
		minutesToSeconds(summary.total_trip_duration_minutes) || windowInfo.totalSeconds;
	const inTransitSeconds = minutesToSeconds(summary.total_in_transit_halt_minutes);

	const nodeDwellSeconds = nodeDwell.reduce((total, halt) => total + halt.durationSeconds, 0);
	const apiUntracked = minutesToSeconds(summary.total_untracked_gap_minutes);
	const dwellSeconds = usingNodeFallback
		? nodeDwellSeconds
		: minutesToSeconds(summary.total_dwell_minutes);
	const untrackedSeconds =
		usingNodeFallback && apiUntracked !== null
			? Math.max(0, apiUntracked - nodeDwellSeconds)
			: apiUntracked;

	return {
		totalSeconds,
		movingSeconds: minutesToSeconds(summary.total_moving_minutes),
		dwellSeconds,
		inTransitSeconds,
		unscheduledSeconds: minutesToSeconds(summary.total_unscheduled_halt_minutes),
		untrackedSeconds,
		unscheduledCount: unscheduled.length,
		halts:
			gpsHalts ||
			[...nodeDwell, ...unscheduled].sort((a, b) =>
				String(a.start ?? '').localeCompare(String(b.start ?? ''))
			),
		// True when rows came from node timestamps rather than a GPS trail, so the section
		// can label them instead of passing derived data off as measured.
		nodeFallback: usingNodeFallback,
		// Only claim completeness when the backend actually listed every halt. On the
		// fallback path the aggregate says there were in-transit halts but no rows
		// describe them — the table has to admit that rather than look complete.
		partial: gpsHalts ? false : Boolean(inTransitSeconds),
		bar: buildStoppageBar({
			totalSeconds,
			moving: minutesToSeconds(summary.total_moving_minutes),
			dwell: dwellSeconds,
			inTransit: inTransitSeconds,
			unscheduled: minutesToSeconds(summary.total_unscheduled_halt_minutes),
			untracked: untrackedSeconds,
		}),
	};
}

/**
 * The tag, client side.
 *
 * Only reached when the reconciliation API has not answered — an open trip before its
 * first poll, a legacy closed trip, or a failed fetch. Precedence is
 * MANUAL_OVERRIDE > NO_SOURCE > coverage-derived, and with no coverage to work from the
 * tag falls back to `final_source` and is marked provisional rather than inventing a
 * coverage figure.
 */
function resolveTag(distances, gps) {
	const flags = distances.varianceFlagged ? ['HIGH_VARIANCE'] : [];

	if (distances.finalSource === 'manual') {
		return { code: DISTANCE_SOURCE_TAG.MANUAL_OVERRIDE, flags, provisional: true };
	}
	if (distances.finalKm === null) {
		return { code: DISTANCE_SOURCE_TAG.NO_SOURCE, flags, provisional: false };
	}

	const coverage = gps.coveragePercent;
	if (coverage === null) {
		const code =
			distances.finalSource === 'gps'
				? DISTANCE_SOURCE_TAG.GPS_VERIFIED
				: DISTANCE_SOURCE_TAG.ODOMETER_ONLY;
		return { code, flags, provisional: true };
	}
	if (coverage >= COVERAGE_VERIFIED_PCT) {
		return { code: DISTANCE_SOURCE_TAG.GPS_VERIFIED, flags, provisional: false };
	}
	if (coverage >= COVERAGE_PARTIAL_PCT) {
		return { code: DISTANCE_SOURCE_TAG.GPS_PARTIAL, flags, provisional: false };
	}
	return { code: DISTANCE_SOURCE_TAG.ODOMETER_ONLY, flags, provisional: false };
}

function buildTag(reconciliation, distances, gps, legacyTrip) {
	const apiCode = API_TAG_CODE[reconciliation?.distance?.distance_source_tag];
	if (!apiCode) {
		const derived = resolveTag(distances, gps);
		return {
			label: null,
			reason: null,
			...derived,
			// "Provisional" promises the tag will firm up. For a legacy trip it never
			// will — the reconciliation row does not exist and is never backfilled — so
			// the two states must not share a label. `unverifiable` says the distance is
			// final but was never GPS-verified, which is the honest description.
			provisional: legacyTrip ? false : derived.provisional,
			unverifiable: legacyTrip,
		};
	}
	return {
		code: apiCode,
		label: null,
		reason: null,
		flags: distances.varianceFlagged ? ['HIGH_VARIANCE'] : [],
		// An open trip's tag is real but not yet locked — it can still change before close.
		provisional: !reconciliation?.is_final,
		unverifiable: false,
	};
}

export default function buildDistanceViewModel({ trackingInfo, tripDetails, reconciliation }) {
	const statusId = num(trackingInfo?.trip_info?.trip_status_id);
	// A trip closed before this feature shipped has no reconciliation row and never will.
	// Its payload carries nothing but this flag, so the tab must say "not available"
	// rather than show a provisional figure that will never become final.
	const legacyTrip = Boolean(reconciliation?.legacy_trip);
	const usable = legacyTrip ? null : reconciliation;

	const windowInfo = buildWindow(trackingInfo, usable);
	const distances = buildDistances(trackingInfo, tripDetails, usable);
	const gps = buildGps(trackingInfo, usable, windowInfo);

	return {
		statusId,
		statusLabel: trackingInfo?.trip_info?.trip_status || null,
		isTerminal: TERMINAL_TRIP_STATUS_IDS.includes(statusId),
		distanceStage: (() => {
			const final = usable?.is_final ?? TERMINAL_TRIP_STATUS_IDS.includes(statusId);
			if (final) return 'final';
			return TERMINAL_TRIP_STATUS_IDS.includes(statusId) ? 'ended' : 'running';
		})(),
		// Tracking has not begun before check-in, so the tab shows the plan and says so.
		notStarted:
			!TERMINAL_TRIP_STATUS_IDS.includes(statusId) &&
			!trackingInfo?.driver_checkin?.checkin_time &&
			!windowInfo.totalSeconds &&
			distances.odometerStart === null &&
			distances.odometerEnd === null &&
			distances.finalKm === null,
		isFinal: usable?.is_final ?? TERMINAL_TRIP_STATUS_IDS.includes(statusId),
		legacyTrip,
		hasReconciliation: Boolean(usable),
		tripType: tripDetails?.tripType || trackingInfo?.trip_info?.trip_type || null,
		isInterCity: Boolean(tripDetails?.interCity || trackingInfo?.trip_info?.is_inter_city),
		isCrossVendor: Boolean(tripDetails?.is_cross_vendor_vehicle),
		vehicleVendorName: tripDetails?.vehicle_vendor_name || null,
		isValid: tripDetails?.isValid !== false,
		vendorRate: num(trackingInfo?.vendor_rate ?? tripDetails?.vendorRate),
		vehicleNumber: trackingInfo?.vehicle_info?.vehicle_number || null,
		totalDurationLabel: trackingInfo?.trip_timings?.total_duration_hrs || null,
		window: windowInfo,
		distances,
		gps,
		tag: buildTag(usable, distances, gps, legacyTrip),
		stoppages: buildStoppages(trackingInfo, usable, windowInfo),
	};
}
